import { useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type PlatformFilter = 'all' | 'twitter' | 'instagram' | 'tiktok' | 'facebook' | 'youtube' | 'direct';

export interface CommunityFeedItem {
  id: string;
  type: 'social' | 'story';
  platform?: string;
  author_handle?: string;
  author_display_name?: string;
  author_avatar_url?: string;
  title?: string;
  text: string;
  html?: string;
  platform_url?: string;
  hospital_name?: string;
  amount_charged?: number;
  amount_expected?: number;
  insurance_status?: string;
  likes_count: number;
  shares_count: number;
  comments_count: number;
  views_count: number;
  is_featured: boolean;
  is_pinned: boolean;
  posted_at: string;
  media: CommunityMediaItem[];
}

export interface CommunityMediaItem {
  id: string;
  media_type: string;
  r2_url?: string;
  original_url?: string;
  width?: number;
  height?: number;
}

const PAGE_SIZE = 20;

async function fetchSocialPosts(page: number, platform?: string) {
  let query = supabase
    .from('social_media_posts')
    .select('*, community_media(*)')
    .eq('moderation_status', 'approved')
    .order('posted_at', { ascending: false })
    .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

  if (platform && platform !== 'all') {
    query = query.eq('platform', platform);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map((post: any): CommunityFeedItem => ({
    id: post.id,
    type: 'social',
    platform: post.platform,
    author_handle: post.author_handle,
    author_display_name: post.author_display_name,
    author_avatar_url: post.author_avatar_url,
    text: post.post_text || '',
    html: post.post_html,
    platform_url: post.platform_url,
    likes_count: post.likes_count || 0,
    shares_count: post.shares_count || 0,
    comments_count: post.comments_count || 0,
    views_count: post.views_count || 0,
    is_featured: post.is_featured || false,
    is_pinned: post.is_pinned || false,
    posted_at: post.posted_at || post.created_at,
    media: (post.community_media || []).map((m: any) => ({
      id: m.id,
      media_type: m.media_type,
      r2_url: m.r2_url,
      original_url: m.original_url,
      width: m.width,
      height: m.height,
    })),
  }));
}

async function fetchDirectStories(page: number) {
  const { data, error } = await supabase
    .from('community_stories')
    .select('*, community_media(*), hospitals(name)')
    .eq('moderation_status', 'approved')
    .order('created_at', { ascending: false })
    .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

  if (error) throw error;
  return (data || []).map((story: any): CommunityFeedItem => ({
    id: story.id,
    type: 'story',
    platform: 'direct',
    author_display_name: story.author_name || 'Anonymous',
    title: story.title,
    text: story.story_text || '',
    hospital_name: story.hospitals?.name,
    amount_charged: story.amount_charged,
    amount_expected: story.amount_expected,
    insurance_status: story.insurance_status,
    likes_count: 0,
    shares_count: 0,
    comments_count: 0,
    views_count: story.view_count || 0,
    is_featured: story.is_featured || false,
    is_pinned: story.is_pinned || false,
    posted_at: story.created_at,
    media: (story.community_media || []).map((m: any) => ({
      id: m.id,
      media_type: m.media_type,
      r2_url: m.r2_url,
      original_url: m.original_url,
      width: m.width,
      height: m.height,
    })),
  }));
}

export function useCommunityFeed(platformFilter: PlatformFilter = 'all') {
  return useInfiniteQuery({
    queryKey: ['community-feed', platformFilter],
    queryFn: async ({ pageParam = 0 }) => {
      if (platformFilter === 'direct') {
        return fetchDirectStories(pageParam);
      }
      if (platformFilter !== 'all') {
        return fetchSocialPosts(pageParam, platformFilter);
      }
      // "all" — merge social posts and direct stories
      const [social, stories] = await Promise.all([
        fetchSocialPosts(pageParam),
        fetchDirectStories(pageParam),
      ]);
      const merged = [...social, ...stories];
      merged.sort((a, b) => new Date(b.posted_at).getTime() - new Date(a.posted_at).getTime());
      return merged;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return allPages.length;
    },
    initialPageParam: 0,
    staleTime: 2 * 60 * 1000,
  });
}
